import {
  clockView,
  followReturn,
  safeZone,
  type ClockView,
  type RaiseClock,
  type Strip,
} from '../raise';
import { aboutDate } from '../raise-about';
import { $, $$, attr, text } from './dom';
import { track } from './events';
import { subscribe, timeZone } from './widgets';

/** What the page reads from and does to the world, for a test to stand in. */
export interface RaiseDeps {
  now: () => number;
  /** The reader's IANA zone, or null where the browser will not say. */
  zone: () => string | null;
  navigate: (url: string) => void;
}

const DEFAULT_DEPS: RaiseDeps = {
  now: () => Date.now(),
  zone: timeZone,
  navigate: (url) => window.location.assign(url),
};

/**
 * The raise page (views/raise.hbs, /raises/{slug}). The server draws it
 * whole, its dates in London's zone or the signed-in reader's saved one;
 * this redraws them on the reader's own clock (clockView, the same function
 * the server ran), keeps a live countdown current once a minute, and runs
 * the follow and the newsletter form.
 */
export function raisePage(
  root: HTMLElement,
  deps: RaiseDeps = DEFAULT_DEPS,
): void {
  let clock: RaiseClock;
  try {
    clock = JSON.parse(root.dataset.clock ?? '') as RaiseClock;
  } catch {
    return;
  }
  const zone = safeZone(deps.zone() ?? root.dataset.zone);
  const draw = () => paint(root, clockView(clock, new Date(deps.now()), zone));
  draw();
  // About this raise's dates, as the reader's own calendar has them.
  for (const el of $$<HTMLTimeElement>(root, 'time[data-rz-date]')) {
    const at = Date.parse(el.dateTime);
    if (!Number.isNaN(at)) text(el, aboutDate(new Date(at), zone));
  }
  if (clock.phase === 'LIVE' && clock.closeAt) {
    const closeAt = Date.parse(clock.closeAt);
    const timer = window.setInterval(() => {
      // Past the close the server's page says what happened, not this clock.
      if (deps.now() >= closeAt) return window.clearInterval(timer);
      draw();
    }, 60_000);
  }

  // Back from log-in or sign-up holding ?follow=. Taken off the address
  // whichever raise it names.
  const wanted = dropFollowParam();
  const id = root.dataset.raiseId ?? '';
  const card = $(root, '[data-follow]');
  if (card && id) {
    follow(root, card, id, deps, wanted, (following) => {
      // A follow made here starts now; one the page opened with keeps its date.
      if (following && !clock.followedAt) {
        clock = { ...clock, followedAt: new Date(deps.now()).toISOString() };
      }
      draw();
      showStrip(root, following);
    });
  }
  const news = $(root, '[data-news]');
  if (news) newsletter(news);
}

/**
 * The reminders strip, while the reader follows the raise and their plan
 * sends its alerts; the alert card's link to the platform while it does not.
 */
function showStrip(root: HTMLElement, following: boolean): void {
  const strip = $(root, '[data-strip]');
  if (!strip) return;
  const shown = following && !('held' in root.dataset);
  strip.hidden = !shown;
  const out = $(root, '[data-follow-out]');
  if (out) out.hidden = shown;
}

/** Writes a clock view into the page's marked elements. */
export function paint(root: HTMLElement, view: ClockView): void {
  const set = (key: string, value: string | null | undefined) =>
    text($(root, `[data-rz="${key}"]`), value ?? '');
  root.dataset.tone = view.pill.tone;
  set('pillGlyph', view.pill.glyph);
  set('pill', view.pill.text);
  set('heading', view.heading);
  attr(
    $(root, '[data-rz-label="countdownLabel"]'),
    'aria-label',
    view.countdownLabel,
  );
  set('dateLine', view.dateLine);
  set('glyph', view.timeLeft?.glyph);
  set('timeLeft', view.timeLeft?.text);
  set('day', view.day?.short);
  set('dayLong', view.day?.long);

  const prov = $(root, '[data-rz="prov"]');
  text(prov, view.provenance.text);
  prov?.classList.toggle('is-stale', view.provenance.stale);
  set('updated', view.updated);

  const track = view.track;
  if (track) {
    set('launched', track.launched);
    set('closes', track.closes);
    set('launchedShort', track.launchedShort);
    set('closesShort', track.closesShort);
    const fill = $(root, '[data-rz-fill]');
    if (fill) fill.style.width = `${track.today}%`;
    const today = $(root, '[data-rz-today]');
    if (today) today.style.left = `${track.today}%`;
    for (const el of $$(root, '[data-rz-mark]')) {
      const mark = track.markers.find((m) => m.kind === el.dataset.rzMark);
      if (!mark) continue;
      el.classList.toggle('is-sent', mark.sent);
      attr(el, 'title', mark.title);
      attr(el, 'aria-label', mark.title);
    }
  }
  const sent = $(root, '[data-rz="sent"]');
  set('sent', view.legend.sent);
  if (sent?.parentElement) sent.parentElement.hidden = !view.legend.sent;
  const next = $(root, '[data-rz="next"]');
  set('next', view.legend.next);
  if (next?.parentElement) next.parentElement.hidden = !view.legend.next;

  // The schedule lists only what is still ahead.
  for (const row of $$(root, '[data-rz-row]')) {
    const entry = view.schedule.find((s) => s.kind === row.dataset.rzRow);
    row.hidden = !entry;
    if (entry) text($(row, '[data-rz-when]'), entry.when);
  }

  paintStrip(root, view.strip);
}

/**
 * The reminders strip. The server drew a node for every reminder the raise
 * has; one before the reader followed is not theirs, and is hidden.
 */
function paintStrip(root: HTMLElement, strip: Strip | null): void {
  const box = $(root, '[data-strip]');
  if (!box) return;
  if (!strip) {
    box.hidden = true;
    return;
  }
  const closes = $(box, '[data-rz-strip-closes]');
  text(closes, strip.closes);
  if (closes) closes.dataset.tone = strip.tone;
  const fill = $(box, '[data-rz-strip-fill]');
  if (fill) fill.style.width = `${strip.fill}%`;
  const now = $(box, '[data-rz-strip-now]');
  if (now) {
    now.hidden = strip.now === null;
    now.style.left = `${strip.now ?? 0}%`;
  }
  for (const el of $$(box, '[data-rz-node]')) {
    const node = strip.nodes.find((n) => n.kind === el.dataset.rzNode);
    el.hidden = !node;
    if (!node) continue;
    el.className = `rz-rem-node is-${node.state}`;
    el.style.left = `${node.pos}%`;
  }
  for (const el of $$(box, '[data-rz-node-label]')) {
    const node = strip.nodes.find((n) => n.kind === el.dataset.rzNodeLabel);
    el.hidden = !node;
    if (!node) continue;
    el.className = `rz-rem-l is-${node.state} is-${node.align}`;
    el.style.setProperty('--pos', `${node.pos}%`);
    text($(el, '[data-rz-node-title]'), node.title);
    text($(el, '[data-rz-node-date]'), node.date);
  }
}

type FollowResult = 'ok' | 'signed-out' | 'failed';

async function save(id: string, on: boolean): Promise<FollowResult> {
  try {
    const res = await fetch(`/api/raises/${encodeURIComponent(id)}/follow`, {
      method: on ? 'PUT' : 'DELETE',
      credentials: 'same-origin',
      headers: { 'x-time-zone': timeZone() ?? '' },
    });
    if (res.ok) return 'ok';
    return res.status === 401 ? 'signed-out' : 'failed';
  } catch {
    return 'failed';
  }
}

const FAILED = 'That didn’t save. Try again in a moment.';

/**
 * Follow and Unfollow, signed in: the card changes at once and goes back if
 * the save fails. Signed out, the links go to sign-up or log-in and come
 * back to this page holding ?follow=, which this follows on arrival.
 *
 * The session is the dashboard's cookie, which lives a minute unless the
 * dashboard is open to refresh it; the page's own load refreshes it (Clerk's
 * handshake). So a 401 reloads the page through the ?follow= return, and a
 * 401 on that return sends the reader to log in.
 */
function follow(
  root: HTMLElement,
  card: HTMLElement,
  id: string,
  deps: RaiseDeps,
  wanted: string | null,
  changed: (following: boolean) => void,
): void {
  const props = { raise_id: id, source: 'deal_page' } as const;
  for (const link of $$(card, '[data-follow-signup], [data-follow-login]')) {
    link.addEventListener('click', () => track('follow_clicked', props));
  }
  if (!('signedIn' in root.dataset)) return;

  const ask = $(card, '[data-follow-in]');
  const done = $(card, '[data-follow-done]');
  const button = $<HTMLButtonElement>(card, '[data-follow-btn]');
  const off = $<HTMLButtonElement>(card, '[data-unfollow-btn]');
  const error = $(card, '[data-follow-error]');
  if (!ask || !done || !button || !off) return;

  const show = (following: boolean) => {
    ask.hidden = following;
    done.hidden = !following;
    changed(following);
  };
  const back = followReturn({
    id,
    slug: decodeURIComponent(location.pathname.split('/').pop() ?? ''),
  });

  const run = async (on: boolean, returning: boolean) => {
    text(error, '');
    show(on);
    const result = await save(id, on);
    if (result === 'ok') {
      if (on) track('follow_created', props);
      (on ? off : button).focus();
      return;
    }
    show(!on);
    if (result === 'signed-out' && on) {
      // The return's 401 means the session itself is gone: sign in again.
      deps.navigate(
        returning ? `/login?redirect_url=${encodeURIComponent(back)}` : back,
      );
      return;
    }
    text(error, FAILED);
  };

  button.addEventListener('click', () => {
    track('follow_clicked', props);
    void run(true, false);
  });
  off.addEventListener('click', () => void run(false, false));

  // Back from sign-up or log-in, or from a 401: follow, once.
  if (wanted === id && done.hidden) void run(true, true);
}

/** Takes ?follow= off the address; answers what it held. */
function dropFollowParam(): string | null {
  const url = new URL(window.location.href);
  const value = url.searchParams.get('follow');
  if (value === null) return null;
  url.searchParams.delete('follow');
  window.history.replaceState(window.history.state, '', url.toString());
  return value;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const INVALID = 'A valid email address is required.';

/**
 * The Weekly Sonar form: the home page's signup (POST /api/subscribers,
 * double opt-in), with its error under the field and its done message in
 * place of the form. "Use a different email" brings the form back. It
 * subscribes to the newsletter only, never a follow of the raise.
 */
function newsletter(root: HTMLElement): void {
  const form = $<HTMLFormElement>(root, '[data-news-form]');
  const input = $<HTMLInputElement>(root, 'input[type="email"]');
  const button = $<HTMLButtonElement>(root, '[data-news-submit]');
  const error = $(root, '[data-news-error]');
  const done = $(root, '[data-news-done]');
  const again = $<HTMLButtonElement>(root, '[data-news-reset]');
  if (!form || !input || !button || !error || !done || !again) return;

  const fail = (message: string) => {
    text(error, message);
    attr(input, 'aria-invalid', 'true');
    input.focus();
  };
  input.addEventListener('input', () => {
    text(error, '');
    input.removeAttribute('aria-invalid');
  });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const email = input.value.trim();
    if (!EMAIL.test(email)) return fail(INVALID);
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    subscribe(email)
      .then((first) => {
        text($(done, '[data-news-email]'), email);
        if (first) text($(done, '[data-news-first]'), first);
        form.hidden = true;
        done.hidden = false;
        done.focus();
      })
      .catch((cause: unknown) => {
        fail(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      });
  });
  again.addEventListener('click', () => {
    done.hidden = true;
    form.hidden = false;
    input.value = '';
    input.focus();
  });
}
